import React, { useEffect, useState, Dispatch, SetStateAction } from 'react'
import {
  CircularProgress,
  Modal,
  Table,
  Input,
  Toggle,
  BatchSearch,
  HeadItemType,
  Icon
} from 'components'
import { getClientHierarchy } from 'services/client/hierarchy'

import { useForm, Controller } from 'react-hook-form'

import * as S from './styles'
import { CustomBoolean } from 'constants/enums'
export interface ClientsListInterface {
  cd_clie: string
  nm_pcro: string
  nr_cnpj_cpf: string
  key: string
  fl_sele: CustomBoolean
  checked?: boolean
  nr_pedi_clie?: string
  text?: boolean
  index: number
}
interface ModalPostOnProps {
  open: boolean
  orderNumber: string
  setOpen: Dispatch<SetStateAction<boolean>>
  handlePostOn: (clients: ClientsListInterface[]) => void
  handleCancel: () => void
}

const ModalPostOn: React.FC<ModalPostOnProps> = ({
  open,
  orderNumber,
  setOpen,
  handlePostOn,
  handleCancel
}) => {
  const [filter, setFilter] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(true)

  const [records, setRecords] = useState<Array<ClientsListInterface>>([])

  const { control, setValue, reset, getValues } = useForm<ClientsListInterface[]>({
    mode: 'onChange',
    reValidateMode: 'onBlur',
    defaultValues: { ...records }
  })

  const {
    control: controlSearch,
    reset: resetSearch,
    watch
  } = useForm({ defaultValues: { search: '' } })
  const search = watch('search')

  const getClientList = async () => {
    setLoading(true)

    const returnApi = await getClientHierarchy({
      nr_pedi_rasc: orderNumber,
      nm_clie: ''
    })
    const returnApiWithSelected = []

    for (const element of returnApi) {
      returnApiWithSelected.push({
        ...element,
        key: `${element.cd_clie}-${element.nr_cnpj_cpf}`
      })
    }
    const newRecords: ClientsListInterface[] = returnApiWithSelected.map(
      (record: ClientsListInterface, index: number) => ({
        ...record,
        checked: false,
        index: index
      })
    )
    setRecords(newRecords)
    setLoading(false)
    return newRecords
  }

  const filterRecords = () => {
    const filteredRecords: Array<ClientsListInterface> = []
    if (search !== '') {
      search
        .split(',')
        .filter((item: string) => item.trim() !== '')
        .forEach((element: string) => {
          filteredRecords.push(
            ...records.filter((item) => {
              return (
                item.nm_pcro.toLowerCase().includes(element.toLowerCase()) ||
                item.cd_clie.toLowerCase().includes(element.toLowerCase())
              )
            })
          )
        })
    } else {
      filteredRecords.push(...records)
    }

    return filteredRecords
  }

  useEffect(() => {
    if (records.length) reset({ ...records })
    else if (!filter.length) getClientList()
  }, [records.length])

  useEffect(() => {
    if (open) {
      getClientList()
    }
    return function cleanup() {
      reset()
      setFilter('')
    }
  }, [open])

  const tableLabels: HeadItemType[] = [
    { label: 'SAP', columnName: 'SAP' },
    { label: 'Razão Social', columnName: 'RazAOSocial' },
    { label: 'Nº Pedido Cliente', columnName: 'NumeroPedidoCliente' },
    { label: 'Texto', columnName: 'Texto' }
  ]

  const setMaterialSelection = (checked: boolean, id?: string) => {
    if (id) {
      const newRecords = records.map((record, index) => {
        if (index === Number(id)) {
          return { ...record, checked: checked }
        }
        return record
      })
      setValue(`${Number(id)}.checked`, checked)
      setRecords(newRecords)
    } else {
      const filteredRecords = filterRecords()
      const newRecords = filteredRecords.map((record) => {
        return { ...record, checked: checked }
      })
      records.forEach((val, key) => {
        if (newRecords[key]?.checked) {
          setValue(`${key}.checked`, checked)
        }
      })
      setRecords(newRecords)
    }
  }
  const getRecordsData = () => {
    const filteredRecords = filterRecords()
    const recordsData = filteredRecords?.map((item, key) => {
      return {
        id: item.index.toString(),
        checked: item?.checked,
        content: [
          item.cd_clie,
          item.nm_pcro,
          <div key={`${item.key}-${key}-nr_pedi_clie`} style={{ marginBottom: -40 }}>
            <Controller
              name={`${key}.nr_pedi_clie`}
              control={control}
              defaultValue=""
              render={({ field: { name, value, onChange } }) => (
                <Input
                  name={name}
                  value={value ?? ''}
                  onChange={onChange}
                  InputProps={{
                    inputProps: { maxLength: 23 }
                  }}
                />
              )}
            />
          </div>,
          <Controller
            key={`${item.key}-${key}-text`}
            name={`${key}.text`}
            control={control}
            defaultValue={true}
            render={({ field: { value } }) => (
              <Toggle
                value={Boolean(value)}
                setValue={() => {
                  return
                }}
                processChange={() => {
                  setValue(`${key}.text`, !value)
                }}
                disabled={false}
              />
            )}
          />
        ]
      }
    })
    return recordsData
  }

  return (
    <Modal
      open={open}
      title="Listar Pedido"
      showReturn
      buttonIcon={<Icon icon="duplicate" size="15" />}
      returnText="Cancelar"
      buttonText="Copiar para selecionados"
      setOpen={setOpen}
      buttonDisabled={
        !Object.values(getValues())?.filter((record) => record.checked).length
      }
      buttonAction={() => {
        const arrayValues = Object.values(getValues())?.filter((record) => record.checked)
        handlePostOn(arrayValues)
      }}
      returnAction={handleCancel}
    >
      <S.ModalContent>
        <S.ModalInfo>
          Selecione para quais clientes deseja copiar o{' '}
          <strong>Pedido #{orderNumber}</strong>
        </S.ModalInfo>
        <Controller
          name="search"
          control={controlSearch}
          render={({ field }) => (
            <BatchSearch
              label="Cliente"
              placeHolder="Buscar por número SAP"
              onClear={() => {
                resetSearch({ search: '' })
              }}
              {...field}
            />
          )}
        />

        {loading ? (
          <CircularProgress />
        ) : (
          <>
            <S.TableLabel>CNPJ&rsquo;s vinculados</S.TableLabel>
            <S.WrapTableStyled>
              <Table
                hasCheckbox
                setChecked={setMaterialSelection}
                headData={tableLabels}
                bodyData={getRecordsData()}
                notFoundTitle="Nenhum cliente encontrado"
                notFoundDescription="Cheque o texto que digitou e tente novamente."
              />
            </S.WrapTableStyled>
          </>
        )}
      </S.ModalContent>
    </Modal>
  )
}

export default ModalPostOn
